import { USER_STATUS } from "../components/common/userStatus";
import { ROLE_DISPLAY } from "../components/pages/GlobalSearch/utils";

// Global user list (ADM-89). The server scopes the rows by role: admins and
// Coach see every org, Org Admins only their org and its sub-orgs. The v3 users
// API knows Org Admin only as FINLEY_ORG_ADMIN (there is no ORG_ADMIN role).
export const USERS_VIEW_ROLES = [
  "SUPER_ADMIN",
  "FINLEY_ADMIN",
  "FINLEY_ORG_ADMIN",
  "FINANCIAL_COACH",
] as const;

// Coach is read-only: no row selection, move or deactivate (the server returns
// 403 for Coach regardless).
export const USERS_MANAGE_ROLES = ["SUPER_ADMIN", "FINLEY_ADMIN", "FINLEY_ORG_ADMIN"] as const;

// Role wording for the page title, "Welcome back, {Organization} {role}".
export const USERS_ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  FINLEY_ADMIN: "Admin",
  FINLEY_ORG_ADMIN: "Org Admin",
  FINANCIAL_COACH: "Coach",
};

export const canViewUsers = (role?: string | null): boolean =>
  !!role && USERS_VIEW_ROLES.includes(role as (typeof USERS_VIEW_ROLES)[number]);

export const canManageUsers = (role?: string | null): boolean =>
  !!role && USERS_MANAGE_ROLES.includes(role as (typeof USERS_MANAGE_ROLES)[number]);

// Roles whose data the server limits to their own org and its sub-orgs.
export const isOrgScopedRole = (role?: string | null): boolean => role === "FINLEY_ORG_ADMIN";

export const USERS_PER_PAGE = 10;

// Column keys accepted by GET /master-data/v3/users `sortBy`.
export const USER_SORT_KEYS = [
  "name",
  "email",
  "organization",
  "lastLogin",
  "role",
  "status",
  "sessionCount",
] as const;
export type UserSortKey = (typeof USER_SORT_KEYS)[number];
export type SortDirection = "asc" | "desc";

// Every role the v3 users API accepts as a `role` filter (anything else is a
// 400), labelled the same way as the table's Role column.
const USER_ROLES = ["SUPER_ADMIN", "FINLEY_ADMIN", "FINLEY_ORG_ADMIN", "FINANCIAL_COACH", "FINLEY_USER"];
export const USER_ROLE_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  ...USER_ROLES.map((role) => ({ value: role, label: ROLE_DISPLAY[role] ?? role })),
];

// Roles each inviter may grant from the invite modal (ADM-92): roles at or
// below the inviter's own. The server enforces the same rules (see the ADM-92
// API addendum); Coach and other roles cannot invite. Finley User comes first
// because most invites are for end users.
const INVITABLE_ROLES_BY_INVITER: Record<string, string[]> = {
  SUPER_ADMIN: ["FINLEY_USER", "FINLEY_ORG_ADMIN", "FINANCIAL_COACH", "FINLEY_ADMIN", "SUPER_ADMIN"],
  FINLEY_ADMIN: ["FINLEY_USER", "FINLEY_ORG_ADMIN", "FINANCIAL_COACH", "FINLEY_ADMIN"],
  FINLEY_ORG_ADMIN: ["FINLEY_USER", "FINLEY_ORG_ADMIN"],
};

export const getInvitableRoleOptions = (inviterRole?: string | null) =>
  (INVITABLE_ROLES_BY_INVITER[inviterRole ?? ""] ?? []).map((role) => ({
    value: role,
    label: ROLE_DISPLAY[role] ?? role,
  }));

// The four statuses from the acceptance criteria ("Never logged in" in the
// Figma is not part of ADM-89).
export const USER_STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: USER_STATUS.ACTIVE, label: "Active" },
  { value: USER_STATUS.SIGNED_UP, label: "Signed Up" },
  { value: USER_STATUS.INVITED, label: "Invited (Pending)" },
  { value: USER_STATUS.DEACTIVATED, label: "Deactivated" },
];

// Why a user was left out of a bulk move/deactivate (see the ADM-89 API doc).
export const BULK_SKIP_REASON_LABELS: Record<string, string> = {
  ALREADY_IN_DESTINATION: "Already exists in the destination organization",
  ALREADY_DEACTIVATED: "Already deactivated",
  OUT_OF_SCOPE: "Outside your organizations",
  NOT_FOUND: "User no longer exists",
  SELF: "You cannot deactivate your own account",
};
